import { configureStore, createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit'
import { createSelector } from '@reduxjs/toolkit';
import { Leg, Visit } from 'types'
import { MIN_CAMERA_DISTANCE, MAX_CAMERA_DISTANCE} from './constants'

interface LegState {
    legs: Leg[]
    stats: {
        totalCount: number,
        totalDistance: number, /* km */
    }
};

const initialLegState: LegState = {
    legs: [],
    stats: {
        totalCount: 0,
        totalDistance: 0,
    }
}

const legSlice = createSlice({
    name: 'legs',
    initialState: initialLegState,
    reducers: {
        addLeg: (state, action: PayloadAction<Leg>) => {
            state.legs.push(action.payload);
            const leg = action.payload;
            state.stats.totalCount += leg.count;
            state.stats.totalDistance += leg.count * leg.distance;
        },
        addLegs: (state, action: PayloadAction<Leg[]>) => {
            state.legs = [...state.legs, ...action.payload];

            for (const leg of action.payload) {
                state.stats.totalCount += leg.count;
                state.stats.totalDistance += leg.count * leg.distance;
            }
        }
    },
});

type CountryData = {
    countryCode: string,
    countryName: string,
    hours: number,
};

interface VisitState {
    visits: Visit[],
    longestVisit: Visit|null,
    stats: {
        numCountries: number,
        countries: CountryData[],
    },
}

const initialVisitState: VisitState = {
    visits: [],
    longestVisit: null,
    stats: {
        numCountries: 0,
        countries: [],
    }
}

function getLongestVisit(visits: Visit[]): Visit|null { 
    let v = null;
    let vh = 0;
    for (let visit of visits) {
        if (visit.hours >= vh) {
            v = visit;
            vh = visit.hours;
        }
    }
    return v;
}

function computeStats(visits: Visit[]): VisitState['stats'] {
    let countries = new Map<string, string>();
    let countryHours = new Map<string, number>();
    for (const visit of visits) {
        countries.set(visit.location.countryCode, visit.location.countryName);
        countryHours.set(visit.location.countryCode, visit.hours + (countryHours.get(visit.location.countryCode) || 0));
    }
    const countryList: CountryData[] = [];
    for (const country of Array.from(countries.entries())) {
        countryList.push({countryCode: country[0], countryName: country[1], hours: (countryHours.get(country[0])||0)});
    }
    countryList.sort((a, b) => (a.hours - b.hours)).reverse();
    return {
        numCountries: countries.size,
        countries: countryList,
    }
}

const visitSlice = createSlice({
    name: 'visits',
    initialState: initialVisitState,
    reducers: {
        addVisit: (state, action: PayloadAction<Visit>) => {
            state.visits.push(action.payload);
            if ((!state.longestVisit) || 
                (action.payload.hours > state.longestVisit.hours)) {
                state.longestVisit = action.payload;
            }

            state.stats = computeStats(state.visits);
        },
        addVisits: (state, action: PayloadAction<Visit[]>) => {
            state.visits = [...state.visits, ...action.payload];
            state.longestVisit = getLongestVisit(state.visits);
            state.stats = computeStats(state.visits);
        },
    },
});

const uiSlice = createSlice({
    name: 'ui',
    initialState: {
        cameraDistance: 0,
        cameraDistanceFactor: 0,
        batchedCameraDistance: 0,
        scaleIndex: 0,
        // Used for cluster sizes.
        scaleFactor: 0,
        mapPOISize: 4,
    },
    reducers: {
        setCameraDistance: (state, action: PayloadAction<number>) => {
            state.cameraDistance = action.payload;
            console.log("Reducer setCameraDistance, with distance", action.payload);
            // Try to make the "factor" on a scale of [0, 1].
            state.cameraDistanceFactor = (action.payload - MIN_CAMERA_DISTANCE) / (MAX_CAMERA_DISTANCE - MIN_CAMERA_DISTANCE);

            // "batches" for "batchedCameraDistance".
            const scales = [0.05, 0.1, 0.4, 1.0];
            for (let i = 0; i < scales.length; i++) {
                if(state.cameraDistanceFactor < scales[i]+0.0001) {
                    if (state.scaleIndex == i) {
                        // No change, we can keep the log clean and prevent
                        // further updates by quitting.
                        return;
                    }
                    state.scaleIndex = i;
                    state.scaleFactor = i / scales.length;
                    state.batchedCameraDistance = scales[i];
                    console.log(`Setting camera distance to # ${i}: ${scales[i]}`)
                    break;
                }
            }

            // This is what is used by rendered components to scale things.
            state.mapPOISize = (state.batchedCameraDistance*4 + 0.25);
        },
    }
});

const store = configureStore({
    reducer: {
        legs: legSlice.reducer,
        visits: visitSlice.reducer,
        ui: uiSlice.reducer,
    }
});

// Infer the `RootState` and `AppDispatch` types from the store itself
export type RootState = ReturnType<typeof store.getState>
// Inferred type: {posts: PostsState, comments: CommentsState, users: UsersState}
export type AppDispatch = typeof store.dispatch

const getMatchingVisits = createSelector(
    [(state: RootState) => state.visits.visits,
    (state: RootState, id_set: Set<string>) => id_set],
    (visits: Visit[], id_set) => visits.filter((visit) => id_set.has(visit.location.id)),
);

const getMatchingLegs = createSelector(
    [(state: RootState) => state.legs.legs,
    (state: RootState, id_set: Set<string>) => id_set],
    (legs: Leg[], id_set) => legs.filter((leg) => id_set.has(leg.departureLocation.id) || id_set.has(leg.arrivalLocation.id)),
);

export { legSlice, visitSlice, uiSlice, getMatchingVisits, getMatchingLegs }
export default store;
